// ============================================================================
// מאגר הידע של מרכז העזרה
// ============================================================================
// המקור היחיד לכל התוכן בדף העזרה. נבנה ישירות מקוד ה-AGI וה-dialplan,
// ולא מטקסטי ההקלטות, כך שהוא משקף את מה שהמערכת עושה בפועל.
//
// id של כל שלוחה = שם ה-context ב-dialplan (למשל main-7-8-4).
// זה מאפשר קישור ישיר: /help?node=main-7-8-4
// ומאפשר בעתיד להזין את אותו מאגר לעוזר ה-AI כבסיס לתשובות.
//
// כששלוחה משתנה בקוד - מעדכנים כאן. אין תוכן עזרה בשום מקום אחר.
//
// variants: תוכן חלופי למערכות מסוימות. כל variant מחליף רק את השדות שבו.
//   when: 'telebip' - המערכת שייכת לסוכן TELEBIP (agent_profile מהשרת)
//   להוספת תנאי חדש: לחשב אותו ב-activeVariants שב-HelpComp.vue.
// ============================================================================

// ----------------------------------------------------------------------------
// תקופות שמירה - לפי הקרונים בשרת (util/Schedules.js, cron/*.js)
// שים לב: העמודים "קבצים שנמחקו" ו"קבצים זמניים" מציגים באנר של 30 יום,
// אבל הקרונים מוחקים אחרי 15. אם משנים את הקרון - לעדכן כאן.
// ----------------------------------------------------------------------------
export const RETENTION = {
  deletedDays: 15,        // deleteFilePermanentlyAfterMonth
  tempDays: 15,           // deleteStuckedFileAfterMonth
  approvalDays: 4,        // records.cron - הודעות שלא אושרו
  listenStatsMonths: 3,   // deleteOldRecordListening.cron
};

// ----------------------------------------------------------------------------
// מי רשאי. מיפוי לתפקידי המנוי בטלפון (Subscriber.role)
// ----------------------------------------------------------------------------
export const ROLES = {
  all: { label: 'כל המשתמשים', short: 'כולם' },
  managers: { label: 'מנהלים ובעלים', short: 'מנהלים' },
  owner: { label: 'בעל המערכת בלבד', short: 'בעלים' },
};

// ----------------------------------------------------------------------------
// מקשים בזמן השמעת הודעות
// שלוש קבוצות, לפי רשימת המקשים הפעילים (escape digits) בכל שלוחה
// ----------------------------------------------------------------------------
const SEEK_KEYS = [
  { key: '1', text: '10 שניות אחורה' },
  { key: '3', text: '10 שניות קדימה' },
  { key: '5', text: 'עצירה, ושוב 5 להמשך' },
  { key: '7', text: '5 דקות אחורה' },
  { key: '9', text: '5 דקות קדימה' },
  { key: '4', text: 'להודעה הקודמת (ישנה יותר)' },
  { key: '6', text: 'להודעה הבאה (חדשה יותר)' },
];

export const PLAYBACK_SETS = {
  full: {
    title: 'מקשי השמעה מלאים',
    where: 'שלוחת ההודעות (1), הודעות שקיבלתי (0-1), הודעות למנהלים (7-7), הודעות ממתינות לאישור (7-9)',
    keys: [
      ...SEEK_KEYS,
      { key: '2', text: 'השמעת אורך ההקלטה' },
      { key: '#', text: 'האצת ההשמעה (עד פי 2)' },
      { key: '*', text: 'האטת ההשמעה (עד חצי מהירות)' },
      { key: '8', text: 'תפריט אפשרויות להודעה הנוכחית' },
      { key: '0', text: 'יציאה מההשמעה' },
    ],
  },
  basic: {
    title: 'מקשי השמעה בסיסיים',
    where: 'הודעות ששלחתי (0-3), קבצים זמניים (0-5)',
    note: 'באזורים האלה אין שינוי מהירות ואין השמעת אורך הקלטה.',
    keys: [
      ...SEEK_KEYS,
      { key: '8', text: 'תפריט אפשרויות להודעה הנוכחית' },
      { key: '0', text: 'יציאה מההשמעה' },
    ],
  },
  favourites: {
    title: 'מקשי השמעה במועדפים',
    where: 'שלוחת המועדפים (10-2)',
    note: 'במועדפים הסולמית משמשת להסרה ולא להאצה.',
    keys: [
      ...SEEK_KEYS,
      { key: '2', text: 'השמעת אורך ההקלטה' },
      { key: '#', text: 'הסרת ההודעה מהמועדפים' },
      { key: '*', text: 'האטת ההשמעה' },
      { key: '0', text: 'יציאה לתפריט הראשי' },
    ],
  },
};

// ----------------------------------------------------------------------------
// דילוג על הקלטות מערכת
// נגזר מסוג הפקודה בקוד:
//   readDigits  = GET DATA   -> כל תפריט מקבל הקשה תוך כדי ההקראה
//   streamFile  = GET OPTION -> ניתן לדלג במקשים שהוגדרו
//   playback    = STREAM FILE ""  -> לא ניתן לדלג
// ----------------------------------------------------------------------------
export const SKIP_RULES = [
  {
    id: 'skip-menus',
    title: 'בכל תפריט אפשר להקיש מיד',
    text: 'אין צורך לחכות לסוף ההקראה. ברגע שמקישים את הבחירה, ההקראה נעצרת והמערכת עוברת לשם. זה נכון לתפריט הראשי, לתפריטי המשנה ולתפריט האפשרויות שבמקש 8.',
    keys: [],
  },
  {
    id: 'skip-guide',
    title: 'מדריך המקשים בכניסה הראשונה',
    text: 'בכניסה הראשונה לשלוחת ההודעות, להודעות שקיבלתי, להודעות למנהלים ולמועדפים מושמע הסבר על מקשי ההשמעה. אפשר לדלג עליו.',
    keys: ['*'],
    nodeId: 'main-1',
  },
  {
    id: 'skip-record-prompt',
    title: '"אנא הקליטו לאחר האות"',
    text: 'כל מקש מדלג על ההנחיה ומתחיל להקליט מיד. תקף בהשארת הודעה, בהודעה אישית ובהודעה למנהלים.',
    keys: ['כל מקש'],
    nodeId: 'main-2',
  },
  {
    id: 'skip-instructions',
    title: 'הוראות השימוש (שלוחה 3)',
    text: 'ההקראה ארוכה. אפשר לעצור אותה ולחזור לתפריט הראשי.',
    keys: ['0'],
    nodeId: 'main-3',
  },
  {
    id: 'skip-badges',
    title: 'הסבר על תגי הפעילות',
    text: 'אפשר לדלג על ההסבר ולחזור לתפריט התגים.',
    keys: ['0'],
    nodeId: 'main-5-8',
  },
  {
    id: 'skip-leave',
    title: 'אזהרת היציאה מהמערכת',
    text: 'אין צורך לשמוע את האזהרה עד הסוף. אפשר לאשר או לבטל תוך כדי.',
    keys: ['1', '0'],
    nodeId: 'main-6',
  },
  {
    id: 'skip-new-private',
    title: 'התראה על הודעה אישית חדשה',
    text: 'ההודעה "קיימת עבורך הודעה חדשה באזור האישי" מושמעת בכניסה. כל מקש מדלג עליה.',
    keys: ['כל מקש'],
    nodeId: 'entry',
  },
  {
    id: 'skip-confirmations',
    title: 'הודעות אישור אחרי שמירה',
    text: '"ההודעה נשמרה בהצלחה", "ההודעה תוזמנה בהצלחה" וכדומה. כל מקש מדלג עליהן.',
    keys: ['כל מקש'],
  },
  {
    id: 'skip-warnings',
    title: 'אזהרות לפני פעולות ניהול',
    text: 'האזהרה לפני הוספת מנהל והאזהרה לפני השתקת המערכת ניתנות לדילוג בכל מקש.',
    keys: ['כל מקש'],
    nodeId: 'main-7',
  },
  {
    id: 'skip-opening',
    title: 'פתיח המערכת והודעה זמנית',
    text: 'תלוי בהגדרה של המנהל בממשק. פתיח "עקיף" מושמע יחד עם התפריט ואפשר להקיש תוך כדי. פתיח "בלתי עקיף" מושמע עד הסוף.',
    keys: [],
    nodeId: 'entry',
    web: { route: '/upload/page1', label: 'ניהול הקלטת תפריט' },
  },
  {
    id: 'skip-ads',
    title: 'פרסומות',
    text: 'תלוי בפרסומת. בחלק מהפרסומות אפשר לדלג בכל מקש, לפעמים רק אחרי השניות הראשונות. בחלקן צריך לשמוע עד הסוף. יש גם פרסומות שבהן הקשה על המקש שמוזכר בפרסומת מחברת למפרסם.',
    keys: [],
    nodeId: 'entry',
  },
  {
    id: 'no-skip',
    title: 'מה אי אפשר לדלג',
    text: 'הודעות קצרות כמו "הפעולה בוצעה בהצלחה", הקראת מספרים, הודעות שגיאה וצליל הביפ בין הודעות מושמעות עד הסוף.',
    keys: [],
  },
];

// ----------------------------------------------------------------------------
// זרימת הכניסה - לא מקש, אלא מה שקורה לפני התפריט
// ----------------------------------------------------------------------------
export const entryNode = {
  id: 'entry',
  key: null,
  title: 'מה קורה כשמתקשרים',
  summary: 'הבדיקות וההודעות שלפני התפריט הראשי',
  role: 'all',
  details: [
    'המערכת מזהה את המתקשר לפי מספר הטלפון. רק מספרים שרשומים במערכת נכנסים. אם המנהל פתח את המערכת לכולם, מתקשר חדש מצטרף אוטומטית.',
    'אם הוגדרה סיסמת כניסה, יש ארבעה ניסיונות להקיש אותה. המנהל יכול להגדיר בממשק מי פטור מהסיסמה.',
    'בשיחה הראשונה מושמעת ברכה, ואחריה הצעה לאשר קבלת צינתוקים. מי שלא אישר ישמע את ההצעה בכל שיחה עד שיאשר.',
    'אם יש הודעה אישית חדשה, מושמעת התראה על כך.',
    'לפני התפריט מושמעים הודעה זמנית ופתיח, אם המנהל הקליט אותם.',
    'פרסומות תלויות בחבילה של המערכת. בחבילה החינמית, ובחלק מהחבילות בתשלום, מתקשר בלי מנוי רב קול שומע פרסומת קצרה, לכל היותר פעם ב-20 דקות: בכניסה למערכת, או במעבר להודעה הבאה בזמן האזנה. ה-20 דקות נספרות גם בין שיחות וגם בין מערכות: מי ששמע פרסומת וניתק, לא ישמע עוד אחת אם יתקשר שוב לפני שעברו 20 דקות, לאותה מערכת או לאחרת. מי שניתק באמצע הפרסומת ישמע אותה בשיחה הבאה. מתקשר עם מנוי רב קול אף פעם לא שומע פרסומות.',
  ],
  optionGroups: [
    {
      title: 'בהצעה לאישור צינתוקים',
      items: [
        { key: '*1', text: 'אישור קבלת צינתוקים על הודעות חדשות' },
        { key: '0', text: 'המשך לתפריט הראשי בלי לאשר' },
      ],
    },
  ],
  notices: [
    'המערכת לא מאפשרת כניסה ממספר חסוי.',
    'אם כל הערוצים של המערכת תפוסים, יושמע שהקו מוגבל במספר מאזינים.',
  ],
  web: { route: '/settings/page1', label: 'הגדרות כלליות' },
  keywords: ['כניסה', 'התחברות', 'סיסמת כניסה', 'פתיח', 'אישור צינתוק', 'חסוי', 'ערוצים', 'מאזינים', 'מערכת פתוחה', 'פרסומת', 'פרסומות', 'רב קול'],
};

// ----------------------------------------------------------------------------
// עץ השלוחות
// ----------------------------------------------------------------------------
export const phoneTree = {
  id: 'main',
  key: null,
  title: 'התפריט הראשי',
  summary: 'נקודת המוצא לכל השלוחות',
  role: 'all',
  details: [
    'אפשר להקיש את מספר השלוחה בכל רגע, גם באמצע ההקראה.',
    'לשלוחות דו-ספרתיות (10, 32) מקישים את שתי הספרות ברצף מהיר.',
    'מכל מקום במערכת, 0 מחזיר לתפריט הראשי.',
  ],
  keywords: ['תפריט', 'ראשי', 'שלוחות'],
  children: [
    // ------------------------------------------------------------------ #
    {
      id: 'main-hash',
      key: '#',
      title: 'המשך מהמקום שעצרתם',
      summary: 'חזרה להודעה ולנקודה שבה הפסקתם להאזין',
      role: 'all',
      details: [
        'המערכת זוכרת היכן עצרתם בשלוחת ההודעות, בהודעות שקיבלתם, בהודעות ששלחתם, במועדפים ובהודעות למנהלים.',
        'אם ההודעה שבה עצרתם נמחקה בינתיים, תשמעו הודעה על כך ותחזרו לתפריט.',
        'בממשק אפשר לקבוע לכל איש קשר מתי נשמר המיקום: רק בניתוק שיחה, או תמיד.',
      ],
      web: { route: '/subscribes-list', label: 'רשימת תפוצה' },
      keywords: ['המשך', 'מיקום אחרון', 'איפה עצרתי', 'חזרה', 'סולמית'],
    },

    // ------------------------------------------------------------------ 0
    {
      id: 'main-0',
      key: '0',
      title: 'האזור האישי',
      summary: 'הודעות אישיות שקיבלתם ושלחתם, והגדרות פרטיות',
      role: 'all',
      keywords: ['אזור אישי', 'פרטי', 'אישי'],
      children: [
        {
          id: 'main-0-1',
          key: '1',
          title: 'הודעות שהשאירו עבורכם',
          summary: 'השמעת ההודעות האישיות שקיבלתם',
          role: 'all',
          playback: 'full',
          details: ['ההשמעה מתחילה מההודעה החדשה ביותר וממשיכה אוטומטית לישנות יותר.'],
          keywords: ['הודעות אישיות', 'קיבלתי', 'דואר קולי', 'תא קולי'],
          children: [
            {
              id: 'main-0-1-8',
              key: '8',
              title: 'אפשרויות להודעה אישית',
              summary: 'בזמן השמעת הודעה, מקש 8',
              role: 'all',
              optionGroups: [
                {
                  title: 'בתפריט האפשרויות',
                  items: [
                    { key: '1', text: 'פרטי השולח ומועד ההקלטה' },
                    { key: '2', text: 'מחיקת ההודעה (1 לאישור)' },
                    { key: '3', text: 'תשובה אישית לשולח' },
                    { key: '4', text: 'שיתוף ההודעה עם מערכת אחרת' },
                    { key: '5', text: 'שמירה במועדפים' },
                    { key: '*', text: 'חזרה להשמעת ההודעות' },
                  ],
                },
              ],
              requires: ['תשובה אישית: המנהל אפשר הקלטת הודעות אישיות', 'שיתוף: המנהל אפשר שיתוף הודעות'],
              keywords: ['מחיקה', 'תשובה', 'שיתוף', 'מועדפים', 'פרטי שולח'],
            },
          ],
        },
        {
          id: 'main-0-2',
          key: '2',
          title: 'שליחת הודעה אישית',
          summary: 'הודעה קולית שרק הנמען שומע',
          role: 'all',
          details: [
            'מקישים את מספר הנמען ובסיום סולמית. לשליחה לכמה נמענים מפרידים בין המספרים בכוכבית, למשל 0501234567*0527654321#.',
            'אי אפשר לשלוח הודעה לעצמכם, או לנמען שחסם קבלת הודעות אישיות.',
          ],
          optionGroups: [
            {
              title: 'אחרי ההקלטה',
              items: [
                { key: '0', text: 'שליחה עם צינתוק לנמען' },
                { key: '4', text: 'שליחה בלי צינתוק' },
                { key: '5', text: 'המשך ההקלטה מהמקום שעצרתם' },
                { key: '1', text: 'השמעת ההקלטה לפני השליחה' },
                { key: '2', text: 'הקלטה מחדש' },
                { key: '3', text: 'מחיקת ההקלטה' },
              ],
            },
          ],
          requires: ['המנהל אפשר הקלטת הודעות אישיות (לכולם או למנהלים בלבד)'],
          skip: 'את ההנחיה "אנא הקליטו לאחר האות" אפשר לדלג בכל מקש.',
          web: { route: '/settings/page1', label: 'הגדרות כלליות' },
          keywords: ['הודעה פרטית', 'שליחה', 'לחבר', 'נמען', 'כמה נמענים'],
        },
        {
          id: 'main-0-3',
          key: '3',
          title: 'הודעות ששלחתם',
          summary: 'השמעת ההודעות האישיות ששלחתם לאחרים',
          role: 'all',
          playback: 'basic',
          details: ['אי אפשר למחוק או לבטל הודעה אישית אחרי שנשלחה. הנמען יכול למחוק אותה אצלו.'],
          keywords: ['שלחתי', 'יוצאות', 'האם שמע'],
          children: [
            {
              id: 'main-0-3-8',
              key: '8',
              title: 'אפשרויות להודעה ששלחתם',
              summary: 'בזמן השמעת הודעה, מקש 8',
              role: 'all',
              optionGroups: [
                {
                  title: 'בתפריט האפשרויות',
                  items: [
                    { key: '1', text: 'לאיזה מספר נשלחה ההודעה' },
                    { key: '2', text: 'האם הנמען האזין, ובאיזה אחוז מההודעה' },
                    { key: '*', text: 'חזרה להשמעת ההודעות' },
                  ],
                },
              ],
              keywords: ['האזין', 'אחוז האזנה', 'נמען'],
            },
          ],
        },
        {
          id: 'main-0-4',
          key: '4',
          title: 'הגדרות אישיות',
          summary: 'חסימת קבלת הודעות אישיות',
          role: 'all',
          keywords: ['חסימה', 'חסום', 'פרטיות'],
          children: [
            {
              id: 'main-0-4-1',
              key: '1',
              title: 'חסימת כל ההודעות האישיות',
              summary: 'אף אחד לא יוכל לשלוח לכם הודעה אישית',
              role: 'all',
              details: ['המערכת מקריאה את המצב הנוכחי, ואז:'],
              optionGroups: [{ title: 'בחירה', items: [{ key: '1', text: 'חסימה' }, { key: '2', text: 'ביטול החסימה' }] }],
              keywords: ['חסימה מלאה'],
            },
            {
              id: 'main-0-4-2',
              key: '2',
              title: 'חסימת מספרים מסוימים',
              summary: 'רשימה אישית של מספרים שלא יוכלו לשלוח לכם',
              role: 'all',
              children: [
                {
                  id: 'main-0-4-2-1',
                  key: '1',
                  title: 'השמעת המספרים החסומים',
                  summary: 'הקשה על 8 בזמן השמעת מספר מסירה אותו מהרשימה',
                  role: 'all',
                  keywords: ['רשימת חסומים', 'הסרת חסימה'],
                },
                {
                  id: 'main-0-4-2-2',
                  key: '2',
                  title: 'הוספת מספר לחסימה',
                  summary: 'מקישים את המספר ובסיום סולמית, ומאשרים ב-1',
                  role: 'all',
                  keywords: ['חסימת מספר'],
                },
              ],
            },
            {
              id: 'main-0-4-3',
              key: '3',
              title: 'ביטול כל החסימות',
              summary: 'פתיחת האזור האישי לקבלת הודעות מכולם',
              role: 'all',
              keywords: ['שחרור', 'ביטול חסימות'],
            },
          ],
        },
        {
          id: 'main-0-5',
          key: '5',
          title: 'קבצים זמניים',
          summary: 'הקלטות שנשמרו כשהשיחה התנתקה לפני הפרסום',
          role: 'all',
          unannounced: true,
          playback: 'basic',
          details: [
            'אם השיחה נותקה באמצע הקלטה, ההקלטה לא הולכת לאיבוד. היא נשמרת כאן ואפשר להשלים ולפרסם אותה.',
            `קבצים זמניים נמחקים אוטומטית אחרי ${RETENTION.tempDays} יום.`,
          ],
          web: { route: '/upload/page4', label: 'קבצים זמניים' },
          keywords: ['זמני', 'ניתוק', 'התנתק', 'נקטע', 'הקלטה שלא נשמרה', 'תקוע'],
          children: [
            {
              id: 'main-0-5-8',
              key: '8',
              title: 'אפשרויות לקובץ זמני',
              summary: 'בזמן השמעת הקובץ, מקש 8',
              role: 'all',
              optionGroups: [
                {
                  title: 'בתפריט האפשרויות',
                  items: [
                    { key: '1', text: 'פרטי ההקלטה' },
                    { key: '2', text: 'העברה לשלוחת ההודעות' },
                    { key: '5', text: 'המשך ההקלטה מהמקום שנקטעה' },
                    { key: '*', text: 'חזרה להשמעה' },
                  ],
                },
                {
                  title: 'בהעברה לשלוחת ההודעות',
                  items: [
                    { key: '1', text: 'העברה ושליחת צינתוק' },
                    { key: '2', text: 'העברה בלי צינתוק' },
                  ],
                },
              ],
              notices: ['במערכת שבה הודעות דורשות אישור מנהל, ההעברה שולחת את ההודעה לאישור.'],
              keywords: ['העברה', 'פרסום', 'המשך הקלטה'],
            },
          ],
        },
      ],
    },

    // ------------------------------------------------------------------ 1
    {
      id: 'main-1',
      key: '1',
      title: 'שלוחת ההודעות',
      summary: 'לוח ההודעות המרכזי של המערכת',
      role: 'all',
      playback: 'full',
      details: [
        'ההשמעה מתחילה מההודעה החדשה ביותר. כשהודעה מסתיימת, המערכת עוברת אוטומטית לזו שלפניה בזמן.',
        'בכניסה הראשונה מושמע הסבר על מקשי ההשמעה. אפשר לדלג עליו בכוכבית.',
        'במערכת שהחבילה שלה כוללת פרסומות, מתקשר בלי מנוי רב קול שומע פרסומת לכל היותר פעם ב-20 דקות, גם בין שיחות וגם בין מערכות. הפרסומת מושמעת במעבר להודעה הבאה ולא באמצע הודעה, ואחריה ההשמעה ממשיכה מאותה הודעה.',
      ],
      skip: 'את מדריך המקשים בכניסה הראשונה אפשר לדלג בכוכבית.',
      web: { route: '/upload/page2', label: 'ניהול שלוחת הודעות' },
      keywords: ['הודעות', 'לוח', 'שמיעה', 'האזנה', 'הקלטות'],
      children: [
        {
          id: 'main-1-8',
          key: '8',
          title: 'אפשרויות להודעה',
          summary: 'בזמן השמעת הודעה, מקש 8',
          role: 'all',
          details: ['מכל אפשרות, כוכבית מחזירה להשמעה מאותה נקודה.'],
          children: [
            {
              id: 'main-1-8-1',
              key: '1',
              title: 'פרטי ההודעה',
              summary: 'מספר הקובץ, מועד ההקלטה וממי הגיעה',
              role: 'all',
              details: ['הודעה שהועלתה מהממשק תזוהה ככזו.'],
              keywords: ['מי הקליט', 'מתי', 'מספר קובץ'],
            },
            {
              id: 'main-1-8-2',
              key: '2',
              title: 'מחיקת ההודעה',
              summary: 'למי שהקליט את ההודעה, ולמנהלים',
              role: 'all',
              details: [
                'מי שהקליט את ההודעה יכול למחוק אותה.',
                'מנהל שמוחק הודעה של מישהו אחר יתבקש להקיש את סיסמת הניהול.',
                `הודעה שנמחקה עוברת לקבצים שנמחקו בממשק, ונמחקת לצמיתות אחרי ${RETENTION.deletedDays} יום.`,
                'אחרי המחיקה אף אחד כבר לא ישמע את ההודעה, גם אם נשלח עליה צינתוק: הצינתוק רק מודיע שיש הודעה חדשה, והוא לא מעביר את ההודעה עצמה.',
              ],
              optionGroups: [{ title: 'באישור המחיקה', items: [{ key: '1', text: 'מחיקה' }, { key: '2', text: 'ביטול וחזרה להודעות' }] }],
              web: { route: '/upload/page3', label: 'קבצים שנמחקו' },
              keywords: ['מחיקה', 'למחוק', 'הסרת הודעה'],
            },
            {
              id: 'main-1-8-3',
              key: '3',
              title: 'אהבתי',
              summary: 'סימון ההודעה ושמיעת מי אהב',
              role: 'all',
              requires: ['המנהל אפשר את אפשרות אהבתי'],
              optionGroups: [
                {
                  title: 'בתפריט אהבתי',
                  items: [
                    { key: '1', text: 'סימון אהבתי' },
                    { key: '2', text: 'ביטול הסימון' },
                    { key: '3', text: 'כמה סימונים קיבלה ההודעה' },
                    { key: '4', text: 'מי סימן אהבתי' },
                    { key: '5', text: 'כמה הודעות אהבתם במערכת' },
                  ],
                },
              ],
              keywords: ['לייק', 'אהבתי', 'סימון'],
            },
            {
              id: 'main-1-8-4',
              key: '4',
              title: 'הקפצה לראש הרשימה',
              summary: 'ההודעה תושמע ראשונה בשלוחה',
              role: 'all',
              details: ['זמין למי שהקליט את ההודעה ולמנהלים.'],
              optionGroups: [
                {
                  title: 'בהקפצה',
                  items: [
                    { key: '1', text: 'הקפצה בלי צינתוק' },
                    { key: '2', text: 'הקפצה עם צינתוק לכל החברים' },
                  ],
                },
              ],
              keywords: ['הקפצה', 'לראש', 'ראשון', 'קידום'],
            },
            {
              id: 'main-1-8-5',
              key: '5',
              title: 'תשובה אישית לשולח',
              summary: 'הודעה אישית למי שהקליט את ההודעה',
              role: 'all',
              requires: ['המנהל אפשר הקלטת הודעות אישיות'],
              keywords: ['תשובה', 'להגיב', 'פרטי'],
            },
            {
              id: 'main-1-8-6',
              key: '6',
              title: 'שיתוף עם מערכת אחרת',
              summary: 'העתקת ההודעה לשלוחת ההודעות של מערכת נוספת',
              role: 'all',
              details: [
                'מקישים את מספר המערכת ובסיום סולמית, ומאשרים ב-1.',
                'השיתוף אפשרי רק למערכת שאתם רשומים בה, ששתי המערכות מאפשרות שיתוף, ושיש במערכת היעד מקום פנוי.',
              ],
              optionGroups: [{ title: 'באישור השיתוף', items: [{ key: '1', text: 'שיתוף בלי צינתוק' }, { key: '2', text: 'שיתוף עם צינתוק לחברי מערכת היעד' }] }],
              requires: ['המנהל אפשר שיתוף הודעות'],
              keywords: ['שיתוף', 'העברה למערכת', 'להעביר', 'שתף'],
            },
            {
              id: 'main-1-8-7',
              key: '7',
              title: 'שמירה במועדפים',
              summary: 'ההודעה תישמר בשלוחה 10',
              role: 'all',
              keywords: ['מועדפים', 'שמירה', 'שמורות'],
            },
            {
              id: 'main-1-8-8',
              key: '8',
              title: 'מי האזין להודעה',
              summary: 'רשימת המאזינים ובדיקת מספר מסוים',
              role: 'all',
              optionGroups: [{ title: 'בתפריט המאזינים', items: [{ key: '1', text: 'השמעת מספרי המאזינים' }, { key: '2', text: 'בדיקה אם מספר מסוים האזין' }] }],
              keywords: ['מאזינים', 'מי שמע', 'האזין'],
            },
            {
              id: 'main-1-8-9',
              key: '9',
              title: 'שליחה למערכת חיצונית',
              summary: 'הפעלת חיבור API שהוגדר לפעולה הזו',
              role: 'all',
              unannounced: true,
              details: ['פועל רק אם הוגדר Webhook לפעולה "בהקשה על 1-8-9" בהגדרות ה-API. אחרת ההקשה מחזירה להשמעה.'],
              web: { route: '/settings/page5', label: 'API' },
              keywords: ['api', 'webhook', 'וובהוק', 'אינטגרציה', 'חיבור חיצוני'],
            },
          ],
        },
      ],
    },

    // ------------------------------------------------------------------ 2
    {
      id: 'main-2',
      key: '2',
      title: 'השארת הודעה',
      summary: 'הקלטת הודעה לשלוחת ההודעות',
      role: 'all',
      details: [
        'מקליטים אחרי הצליל ומסיימים בסולמית.',
        'אם השיחה מתנתקת לפני הפרסום, ההקלטה נשמרת בקבצים הזמניים (0-5) ואפשר להשלים אותה.',
      ],
      optionGroups: [
        {
          title: 'אחרי ההקלטה',
          items: [
            { key: '0', text: 'פרסום עכשיו ושליחת צינתוק לכולם' },
            { key: '4', text: 'פרסום עכשיו בלי צינתוק' },
            { key: '6', text: 'תזמון פרסום עם צינתוק' },
            { key: '7', text: 'תזמון פרסום בלי צינתוק' },
            { key: '5', text: 'המשך ההקלטה' },
            { key: '1', text: 'השמעה לפני הפרסום' },
            { key: '2', text: 'הקלטה מחדש' },
            { key: '3', text: 'מחיקת ההקלטה' },
          ],
        },
        {
          title: 'בתזמון',
          items: [
            { key: '1', text: 'היום' },
            { key: '2', text: 'מחר' },
            { key: '3', text: 'בעוד יומיים' },
          ],
          note: 'אחר כך מקישים שעה בארבע ספרות, למשל 0930 לשעה 9:30. לא ניתן לתזמן לשבת.',
        },
        {
          title: 'אחרי הפרסום',
          items: [
            { key: '9', text: 'הגבלת זמן: ההודעה תימחק אחרי שעה, 24 שעות, שבוע או חודש' },
            { key: '0', text: 'חזרה לתפריט הראשי' },
          ],
        },
      ],
      notices: [
        'במערכת שבה הודעות דורשות אישור מנהל, משתמש רגיל שולח את ההודעה לאישור במקש 0.',
        'צינתוק נשלח רק למי שאישר קבלת צינתוקים ולא השתיק את עצמו, ולא בזמני השתקה של המערכת.',
      ],
      requires: ['לפי הרשאות ההקלטה: כולם, מנהלים בלבד, או כולם באישור מנהל'],
      skip: 'את ההנחיה "אנא הקליטו לאחר האות" אפשר לדלג בכל מקש, וההקלטה תתחיל מיד.',
      web: { route: '/upload/page2', label: 'ניהול שלוחת הודעות' },
      keywords: ['הקלטה', 'להקליט', 'הודעה חדשה', 'פרסום', 'תזמון', 'מתוזמן', 'הגבלת זמן', 'אישור מנהל', 'צינתוק'],
    },

    // ------------------------------------------------------------------ 3
    {
      id: 'main-3',
      key: '3',
      title: 'העוזר הקולי / הוראות שימוש',
      summary: 'עוזר קולי חכם, או הוראות שימוש מוקלטות - לפי החבילה',
      role: 'all',
      // שני מצבים לשלוחה. בדף העזרה מוצג ליד כל אחד המצב במערכת של הקורא (ai_access מהשרת)
      modes: [
        {
          id: 'ai',
          title: 'העוזר הקולי',
          icon: 'mdi-robot-outline',
          who: 'לפי החבילה של המערכת: בחלק מהחבילות לכל המתקשרים, ובחלק רק למתקשרים עם מנוי רב קול אישי - במערכות שבהן העוזר זמין ומופעל',
          details: [
            'מדברים עם העוזר בשפה חופשית ושואלים איך עושים משהו במערכת. הוא מסביר אילו מקשים להקיש, לפי ההרשאות שלכם ולפי ההגדרות של המערכת שלכם.',
            'העוזר יכול גם לבדוק בשבילכם: כמה חברים יש בקבוצה, מי המנהלים (שמות וטלפונים), כמה אנשים מחוברים עכשיו לקו, אם אפשר להיכנס עכשיו לחדר הוועידה, מה מצב הצינתוקים וההשתקה שלכם, וכמה זמן נשאר לכם לשיחה עם העוזר.',
            'העוזר רק מסביר, ולא מבצע פעולות במערכת.',
            'כדי לחזור לתפריט הראשי אומרים "חזרה לתפריט". הזמן עם העוזר בכל שיחה מוגבל.',
          ],
        },
        {
          id: 'instructions',
          title: 'הוראות שימוש',
          icon: 'mdi-playlist-play',
          who: 'לכל שאר המערכות, וכשהעוזר כבוי בהגדרות',
          details: ['הסבר קולי מוקלט על מקשי ההשמעה.'],
          skip: 'אפשר לעצור את ההקראה ב-0 ולחזור לתפריט.',
        },
      ],
      web: { route: '/settings/page1', label: 'הגדרות כלליות (הפעלה וכיבוי העוזר)' },
      keywords: ['עוזר', 'עוזר קולי', 'AI', 'בינה מלאכותית', 'לשאול', 'שאלה', 'הוראות', 'עזרה', 'הסבר', 'מדריך', 'חבילה בתשלום', 'רב קול', 'כמה מחוברים', 'מי המנהלים', 'חדר ועידה', 'מצב השתקה'],
    },

    // ------------------------------------------------------------------ 4
    {
      id: 'main-4',
      key: '4',
      title: 'השתקת צינתוקים',
      summary: 'הפסקת ההתראות על הודעות חדשות, רק עבורכם',
      role: 'all',
      details: [
        'ההשתקה אישית ולא משפיעה על משתמשים אחרים.',
        'השתקה בשעות קבועות בכל יום, כמו כל לילה, קיימת רק למערכת כולה, והמנהלים קובעים אותה ב-7-8-4 או באתר.',
      ],
      web: { route: '/subscribes-list', label: 'רשימת תפוצה (הגדרות צינתוק)' },
      keywords: ['השתקה', 'צינתוק', 'התראות', 'שקט', 'להפסיק', 'מטריד'],
      children: [
        { id: 'main-4-1', key: '1', title: 'השתקה לכמה שעות', summary: 'עד 20 שעות. מעבר לזה בוחרים ימים', role: 'all', keywords: ['שעות'] },
        { id: 'main-4-2', key: '2', title: 'השתקה לכמה ימים', summary: 'מקישים את מספר הימים ובסיום סולמית', role: 'all', keywords: ['ימים'] },
        { id: 'main-4-3', key: '3', title: 'השתקה ללא הגבלת זמן', summary: 'עד שתבטלו', role: 'all', keywords: ['לצמיתות', 'תמיד'] },
        { id: 'main-4-4', key: '4', title: 'ביטול ההשתקה', summary: 'חזרה לקבלת צינתוקים', role: 'all', keywords: ['ביטול השתקה', 'לחזור לקבל'] },
      ],
    },

    // ------------------------------------------------------------------ 5
    {
      id: 'main-5',
      key: '5',
      title: 'פרטי הקבוצה',
      summary: 'חברי המערכת, מנהלים, הודעה למנהלים ותגי פעילות',
      role: 'all',
      keywords: ['קבוצה', 'חברים', 'מנויים'],
      children: [
        { id: 'main-5-1', key: '1', title: 'מספר החברים', summary: 'כמה חברים רשומים במערכת', role: 'all', keywords: ['כמה חברים', 'כמות'] },
        {
          id: 'main-5-2', key: '2', title: 'בדיקה אם מספר חבר', summary: 'מקישים מספר ובסיום סולמית', role: 'all',
          requires: ['המנהל אפשר השמעת מספרי החברים'], keywords: ['בדיקת מספר', 'רשום'],
        },
        {
          id: 'main-5-3', key: '3', title: 'השמעת מספרי החברים', summary: 'כל המספרים ברצף, עם ביפ ביניהם', role: 'all',
          requires: ['המנהל אפשר השמעת מספרי החברים'], keywords: ['רשימת חברים', 'מספרים'],
        },
        { id: 'main-5-4', key: '4', title: 'מספרי המנהלים', summary: 'מי מנהל את המערכת', role: 'all', keywords: ['מנהל', 'למי לפנות'] },
        {
          id: 'main-5-5', key: '5', title: 'הודעה למנהלים', summary: 'הקלטה שרק המנהלים שומעים', role: 'all',
          details: ['המנהלים שומעים אותה בתפריט הניהול, בשלוחה 7-7.'],
          optionGroups: [
            {
              title: 'אחרי ההקלטה',
              items: [
                { key: '0', text: 'שליחה עם צינתוק למנהלים' },
                { key: '4', text: 'שליחה בלי צינתוק' },
                { key: '1', text: 'השמעה לפני השליחה' },
                { key: '2', text: 'הקלטה מחדש' },
                { key: '3', text: 'מחיקה' },
              ],
            },
          ],
          keywords: ['פנייה למנהל', 'הודעה למנהל'],
        },
        {
          id: 'main-5-6', key: '6', title: 'מי לא אישר צינתוקים', summary: 'דורש סיסמת ניהול', role: 'managers',
          optionGroups: [{ title: 'אחרי הסיסמה', items: [{ key: '1', text: 'השמעת כל המספרים שלא אישרו' }, { key: '2', text: 'בדיקת מספר מסוים' }] }],
          keywords: ['לא אישרו', 'צינתוק', 'מושתקים'],
        },
        {
          id: 'main-5-7', key: '7', title: 'הוספת חברים', summary: 'צירוף מספרים חדשים לקבוצה', role: 'all',
          details: ['מקישים את המספרים עם כוכבית ביניהם, ובסיום סולמית.'],
          requires: ['המנהל אפשר לכל משתמש להוסיף חברים'],
          web: { route: '/settings/page1', label: 'הגדרות כלליות' },
          keywords: ['הוספה', 'לצרף', 'חבר חדש'],
        },
        {
          id: 'main-5-8', key: '8', title: 'תגי פעילות', summary: 'הכרה במשתמשים פעילים', role: 'all', unannounced: true,
          details: [
            'תורם משמעותי: מי ששלח יותר מ-50 הודעות. מאזין חרוץ: מי שהאזין ליותר מ-100 הודעות.',
            'בכל שבוע מוענקים גם שני תגים לשולח הפעיל ביותר ולמאזין הפעיל ביותר.',
          ],
          skip: 'את ההסבר על התגים אפשר לדלג ב-0.',
          keywords: ['תגים', 'דירוג', 'פעילות', 'שיא', 'תורם', 'מאזין חרוץ'],
          children: [
            { id: 'main-5-8-1', key: '1', title: 'התגים שלי', summary: 'אילו תגים קיבלתם', role: 'all', keywords: ['התגים שלי'] },
            {
              id: 'main-5-8-2', key: '2', title: 'מי קיבל תגים', summary: 'המובילים בקבוצה', role: 'all',
              optionGroups: [{
                title: 'בחירה',
                items: [
                  { key: '1', text: 'מי שלח הכי הרבה הודעות השבוע' },
                  { key: '2', text: 'מי האזין להכי הרבה הודעות השבוע' },
                  { key: '3', text: 'מי קיבל תג תורם משמעותי' },
                  { key: '4', text: 'מי קיבל תג מאזין חרוץ' },
                ],
              }],
              keywords: ['מובילים', 'שיאנים'],
            },
            { id: 'main-5-8-3', key: '3', title: 'הסבר על התגים', summary: 'איך מקבלים כל תג', role: 'all', keywords: ['הסבר תגים'] },
          ],
        },
      ],
    },

    // ------------------------------------------------------------------ 6
    {
      id: 'main-6',
      key: '6',
      title: 'יציאה מהמערכת',
      summary: 'הסרת המספר שלכם מהקבוצה',
      role: 'all',
      details: ['אחרי היציאה לא תוכלו להתקשר למערכת. בעל המערכת לא יכול להסיר את עצמו.'],
      optionGroups: [{ title: 'באזהרה', items: [{ key: '1', text: 'אישור היציאה' }, { key: '0', text: 'ביטול וחזרה לתפריט' }] }],
      skip: 'אפשר לאשר או לבטל תוך כדי האזהרה.',
      keywords: ['יציאה', 'הסרה', 'לעזוב', 'ביטול מנוי'],
    },

    // ------------------------------------------------------------------ 7
    {
      id: 'main-7',
      key: '7',
      title: 'ניהול המערכת',
      summary: 'תפריט המנהלים, אחרי סיסמת ניהול',
      role: 'managers',
      details: [
        'משתמש רגיל שמקיש 7 יחזור לתפריט הראשי.',
        'את סיסמת הניהול מקישים פעם אחת בשיחה. אם הסיסמה שגויה, מוצעות מיד שתי אפשרויות: לשחזר את הסיסמה, או לחזור לתפריט הראשי.',
      ],
      optionGroups: [
        {
          title: 'אחרי סיסמה שגויה',
          items: [
            { key: '1', text: 'קבלת שיחה עם הסיסמה. הסיסמה היא הספרות האחרונות של המספר שמתקשר אליכם' },
            { key: '2', text: 'חזרה לתפריט הראשי' },
          ],
        },
      ],
      web: { route: '/account', label: 'אזור אישי (סיסמה טלפונית)' },
      keywords: ['ניהול', 'מנהל', 'סיסמת ניהול', 'שכחתי סיסמה'],
      children: [
        {
          id: 'main-7-1', key: '1', title: 'הוספת חברים', summary: 'רישום מספרים חדשים למערכת', role: 'managers',
          details: ['מקישים את המספרים עם כוכבית ביניהם ובסיום סולמית, ומאשרים ב-1. אחר כך בוחרים קידומת מדינה.'],
          optionGroups: [{ title: 'בבחירת קידומת', items: [{ key: '1', text: 'ישראל' }, { key: '2', text: 'מדינה אחרת מתוך רשימה' }] }],
          notices: ['מספר אנשי הקשר מוגבל לפי החבילה.'],
          web: { route: '/subscribes-list', label: 'רשימת תפוצה' },
          keywords: ['הוספת מנויים', 'רישום', 'לצרף', 'קידומת', 'חו"ל'],
        },
        {
          id: 'main-7-2', key: '2', title: 'הסרת חברים', summary: 'מחיקת מספרים מהמערכת', role: 'managers',
          details: ['מקישים את המספרים עם כוכבית ביניהם ובסיום סולמית, ומאשרים ב-1.'],
          web: { route: '/subscribes-list', label: 'רשימת תפוצה' },
          keywords: ['הסרת מנויים', 'מחיקת חבר'],
        },
        {
          id: 'main-7-3', key: '3', title: 'הוספת מנהלים', summary: 'מינוי מספרים למנהלים', role: 'managers',
          details: ['למנהל חדש יש כמעט את כל הסמכויות: הוספה והסרה של חברים, מחיקת הודעות, שינוי הגדרות ועוד. הוא לא יכול להסיר את בעל המערכת או להגדיר סיסמת כניסה.'],
          skip: 'את האזהרה שלפני ההוספה אפשר לדלג בכל מקש.',
          keywords: ['מנהל חדש', 'מינוי', 'הרשאת ניהול'],
        },
        { id: 'main-7-4', key: '4', title: 'הסרת מנהלים', summary: 'ביטול הרשאת ניהול', role: 'managers', keywords: ['הסרת מנהל'] },
        {
          id: 'main-7-5', key: '5', title: 'הקלטות התפריט', summary: 'פתיח המערכת והודעה זמנית', role: 'managers',
          web: { route: '/upload/page1', label: 'ניהול הקלטת תפריט' },
          keywords: ['פתיח', 'הודעת פתיחה', 'הודעה זמנית', 'תפריט'],
          children: [
            {
              id: 'main-7-5-2', key: '2', title: 'הודעת פתיחה', summary: 'מושמעת לכל מתקשר לפני התפריט', role: 'managers',
              optionGroups: [
                { title: 'בתפריט הפתיח', items: [{ key: '1', text: 'הקלטה' }, { key: '2', text: 'מחיקת הקיימת' }] },
                { title: 'אחרי ההקלטה', items: [{ key: '1', text: 'אישור' }, { key: '2', text: 'הקלטה מחדש' }, { key: '3', text: 'השמעה' }, { key: '0', text: 'ביטול' }] },
              ],
              notices: ['האם אפשר לדלג על הפתיח (עקיף או בלתי עקיף) נקבע בממשק.'],
              keywords: ['פתיח', 'ברוכים הבאים'],
            },
            {
              id: 'main-7-5-3', key: '3', title: 'הודעה זמנית', summary: 'הודעה לתקופה מוגבלת, עם תאריך סיום', role: 'managers',
              optionGroups: [
                { title: 'בתפריט ההודעה הזמנית', items: [{ key: '1', text: 'הקלטה' }, { key: '2', text: 'מחיקת הקיימת' }, { key: '3', text: 'הגדרת מועד סיום' }] },
                { title: 'במועד הסיום', items: [{ key: '1', text: 'בעוד מספר שעות' }, { key: '2', text: 'בעוד מספר ימים' }] },
              ],
              keywords: ['זמנית', 'עדכון', 'מועד סיום', 'תוקף'],
            },
          ],
        },
        {
          id: 'main-7-6', key: '6', title: 'אבטחה וסיסמאות', summary: 'סיסמת הניהול וסיסמת הכניסה', role: 'managers',
          keywords: ['אבטחה', 'סיסמה'],
          children: [
            { id: 'main-7-6-1', key: '1', title: 'שינוי סיסמת הניהול', summary: '4 עד 6 ספרות, ובסיום סולמית', role: 'managers', keywords: ['החלפת סיסמה', 'סיסמת ניהול'] },
            {
              id: 'main-7-6-2', key: '2', title: 'הגדרת סיסמת כניסה', summary: 'סיסמה שכל מתקשר יקיש בכניסה', role: 'owner',
              details: ['4 עד 6 ספרות. בממשק אפשר לקבוע מי פטור ממנה.'],
              web: { route: '/settings/page1', label: 'הגדרות כלליות' },
              keywords: ['סיסמת כניסה', 'נעילה', 'סיסמת מערכת'],
            },
            {
              id: 'main-7-6-3', key: '3', title: 'ביטול סיסמת הכניסה', summary: 'אישור ב-1, ביטול ב-2', role: 'managers',
              keywords: ['הסרת סיסמה', 'ביטול סיסמה'],
            },
          ],
        },
        {
          id: 'main-7-7', key: '7', title: 'הודעות למנהלים', summary: 'הודעות שחברים השאירו למנהלים', role: 'managers',
          playback: 'full',
          keywords: ['הודעות למנהל', 'פניות'],
          children: [
            {
              id: 'main-7-7-8', key: '8', title: 'אפשרויות להודעה', summary: 'בזמן השמעת הודעה, מקש 8', role: 'managers',
              optionGroups: [{
                title: 'בתפריט האפשרויות',
                items: [
                  { key: '1', text: 'פרטי השולח' },
                  { key: '2', text: 'מחיקה' },
                  { key: '3', text: 'תשובה אישית לשולח' },
                  { key: '4', text: 'שיתוף עם מערכת אחרת' },
                  { key: '5', text: 'שמירה במועדפים' },
                  { key: '*', text: 'חזרה להשמעה' },
                ],
              }],
              keywords: ['תשובה', 'מחיקה'],
            },
          ],
        },
        {
          id: 'main-7-8', key: '8', title: 'הגדרות מערכת', summary: 'שפה, הרשאות הקלטה וחסימת מספרים, שדרוג חבילה, השתקה, קווים, חדר ועידה, מחיקה אוטומטית וסקרים', role: 'managers',
          web: { route: '/settings/page1', label: 'הגדרות כלליות' },
          keywords: ['הגדרות', 'הגדרות מערכת'],
          children: [
            {
              id: 'main-7-8-1', key: '1', title: 'שפת המערכת', summary: 'השפה של כל ההקלטות', role: 'managers',
              optionGroups: [{ title: 'בחירה', items: [{ key: '1', text: 'עברית' }, { key: '2', text: 'אידיש (ישראל)' }] }],
              keywords: ['שפה', 'אידיש', 'עברית'],
            },
            {
              id: 'main-7-8-2', key: '2', title: 'הרשאות הקלטה', summary: 'מי יכול להשאיר הודעות בשלוחה 2', role: 'managers',
              optionGroups: [{
                title: 'בחירה',
                items: [
                  { key: '1', text: 'כל המשתמשים' },
                  { key: '2', text: 'מנהלים בלבד' },
                  { key: '3', text: 'כל המשתמשים, באישור מנהל' },
                ],
              }],
              details: [`הודעה שממתינה לאישור ולא אושרה נמחקת אחרי ${RETENTION.approvalDays} ימים.`, 'לחסימת מספר מסוים מהקלטה מקישים 4.'],
              keywords: ['הרשאות', 'מי מקליט', 'אישור מנהל', 'רק מנהלים'],
              children: [
                {
                  id: 'main-7-8-2-4', key: '4', title: 'חסימת מספר מהקלטה', summary: 'מניעת השארת הודעות ממספר מסוים', role: 'managers',
                  details: ['מקישים את המספר ובסיום סולמית, מאשרים ב-1, ואז:'],
                  optionGroups: [{ title: 'בחירה', items: [{ key: '1', text: 'חסימה' }, { key: '2', text: 'ביטול החסימה' }] }],
                  keywords: ['חסימה', 'מטריד', 'לחסום', 'חסימת מספר'],
                },
              ],
            },
            {
              id: 'main-7-8-3', key: '3', title: 'שדרוג החבילה', summary: 'מעבר לחבילה בתשלום חודשי, עם כרטיס אשראי', role: 'managers',
              details: [
                'מוצגות רק החבילות בתשלום שזמינות למערכת. במערכת של סוכן - רק החבילות של הסוכן.',
                'אם השימוש הנוכחי באחסון או במספר אנשי הקשר גדול מהמותר בחבילה החדשה, אי אפשר לעבור אליה.',
                'אם בחבילה החדשה משהו יורד (ערוצים, אחסון, אנשי קשר, פרסומות, העוזר הקולי), שומעים מה בדיוק לפני שממשיכים.',
                'מקישים את תעודת הזהות של בעל הכרטיס, את מספר הכרטיס, את התוקף ואת 3 הספרות שבגב הכרטיס, ומאשרים את הסכום החודשי.',
                'הכרטיס נבדק בתפיסת מסגרת של שקל אחד, שמשתחררת ולא מחויבת. החיוב החודשי הראשון מתבצע כמה דקות אחרי השדרוג.',
                'אם כבר יש הוראת קבע, היא מתעדכנת לחבילה החדשה, ולא נוצרת הוראה נוספת.',
                'אחרי 3 ניסיונות שנכשלו באותו יום, אפשר לנסות שוב רק למחרת.',
              ],
              web: { route: '/settings/page4', label: 'פרטי חשבון' },
              keywords: ['שדרוג', 'שדרוג חבילה', 'החלפת חבילה', 'חבילה בתשלום', 'אשראי', 'כרטיס אשראי', 'הוראת קבע', 'תשלום'],
            },
            {
              id: 'main-7-8-4', key: '4', title: 'השתקת צינתוקים למערכת', summary: 'אף משתמש לא יקבל התראות בזמן ההשתקה', role: 'managers',
              skip: 'את האזהרה שלפני ההשתקה אפשר לדלג בכל מקש.',
              details: ['בחלק מהאפשרויות בוחרים גם אם ההשתקה כוללת הודעות אישיות, או רק את שלוחת ההודעות.'],
              web: { route: '/settings/page2', label: 'צינתוק' },
              keywords: ['השתקת מערכת', 'לילה', 'שבת', 'צינתוק', 'שקט'],
              children: [
                { id: 'main-7-8-4-1', key: '1', title: 'לכמה שעות', summary: 'עד 20 שעות', role: 'managers', keywords: ['שעות'] },
                { id: 'main-7-8-4-2', key: '2', title: 'לכמה ימים', summary: 'מקישים מספר ימים', role: 'managers', keywords: ['ימים'] },
                { id: 'main-7-8-4-3', key: '3', title: 'כל לילה', summary: 'בכל יום מ-21:00 עד 07:00', role: 'managers', keywords: ['לילה', 'קבוע'] },
                { id: 'main-7-8-4-4', key: '4', title: 'ללא הגבלת זמן', summary: 'עד לביטול', role: 'managers', keywords: ['לצמיתות'] },
                { id: 'main-7-8-4-5', key: '5', title: 'ביטול כל ההשתקות', summary: 'חזרה לשליחת צינתוקים', role: 'managers', keywords: ['ביטול'] },
              ],
            },
            {
              id: 'main-7-8-5', key: '5', title: 'קווים וזיהוי צינתוק', summary: 'הקווים המחוברים ואורך הצינתוק', role: 'managers',
              keywords: ['קווים', 'זיהוי', 'מספר מזהה'],
              children: [
                { id: 'main-7-8-5-1', key: '1', title: 'השמעת הקווים', summary: 'מספרי הטלפון המחוברים למערכת', role: 'managers', keywords: ['מספרי הקו'] },
                {
                  id: 'main-7-8-5-2', key: '2', title: 'מספר מזהה לצינתוק', summary: 'איזה מספר יופיע אצל המשתמשים', role: 'managers',
                  details: ['המספר חייב להיות אחד הקווים של המערכת.'],
                  web: { route: '/settings/page2', label: 'צינתוק' },
                  keywords: ['זיהוי שיחה', 'caller id', 'מספר מזהה'],
                },
                {
                  id: 'main-7-8-5-3', key: '3', title: 'דיווח על מספר חסום', summary: 'כשהמספר המזהה נחסם אצל המשתמשים', role: 'managers',
                  details: ['הדיווח נשלח לבדיקה. אם יאומת, יוקצה מספר חדש.'],
                  keywords: ['נחסם', 'ספאם', 'חסום אצל'],
                },
                {
                  id: 'main-7-8-5-4', key: '4', title: 'אורך הצינתוק', summary: '5, 6, 7, 8, 9, 10, 15, 20 או 25 שניות', role: 'managers',
                  web: { route: '/settings/page2', label: 'צינתוק' },
                  keywords: ['זמן צלצול', 'אורך', 'שניות'],
                },
              ],
            },
            {
              id: 'main-7-8-6', key: '6', title: 'גישה למערכת', summary: 'רשומים בלבד, או פתוחה לכולם', role: 'managers',
              optionGroups: [{ title: 'בחירה', items: [{ key: '1', text: 'חברים רשומים בלבד' }, { key: '2', text: 'פתוחה לכולם: מתקשר חדש מצטרף אוטומטית' }] }],
              keywords: ['פתוח', 'סגור', 'גישה', 'הצטרפות אוטומטית'],
            },
            {
              id: 'main-7-8-7', key: '7', title: 'חדר הוועידה', summary: 'מי פותח את החדר וצליל כניסה', role: 'managers',
              keywords: ['ועידה', 'שיחת ועידה'],
              children: [
                {
                  id: 'main-7-8-7-1', key: '1', title: 'מי פותח את החדר', summary: 'כולם, או מנהלים בלבד', role: 'managers',
                  optionGroups: [{ title: 'בחירה', items: [{ key: '1', text: 'כולם' }, { key: '2', text: 'מנהלים בלבד' }] }],
                  keywords: ['פתיחת חדר'],
                },
                {
                  id: 'main-7-8-7-2', key: '2', title: 'צליל כניסה ויציאה', summary: 'צליל כשמשתתף מצטרף או עוזב', role: 'managers',
                  optionGroups: [{ title: 'בחירה', items: [{ key: '1', text: 'השמעת צליל' }, { key: '2', text: 'בלי צליל' }] }],
                  keywords: ['צליל', 'צפצוף'],
                },
              ],
            },
            {
              id: 'main-7-8-8', key: '8', title: 'מחיקה אוטומטית של הודעות', summary: 'הודעות נמחקות 30 יום אחרי ההקלטה', role: 'managers',
              details: [
                'קודם מושמע אם המחיקה האוטומטית פעילה במערכת, ואז מקש 1 מפעיל או מבטל אותה.',
                `כשהמחיקה פעילה, כל לילה הודעות שהוקלטו לפני יותר מ-30 יום עוברות לקבצים שנמחקו, ונמחקות לצמיתות אחרי ${RETENTION.deletedDays} יום נוספים.`,
                'שאלות הסקרים לא נמחקות במחיקה הזו.',
              ],
              web: { route: '/settings/page1', label: 'אבטחה ופרטיות' },
              keywords: ['מחיקה אוטומטית', '30 יום', 'מחיקת הודעות', 'ניקוי הודעות', 'פרטיות'],
            },
            {
              id: 'main-7-8-9', key: '9', title: 'מי יכול ליצור סקרים', summary: 'כל המאזינים, או מנהלים בלבד', role: 'managers',
              details: [
                'קודם מושמע מי יכול ליצור סקרים כרגע, ואז מקש 1 מחליף בין כל המאזינים למנהלים בלבד.',
                'מנהלים ובעלים יכולים ליצור סקרים תמיד. מי שיוצר סקר יכול גם לשלוח עליו צינתוק לכל החברים.',
              ],
              requires: ['שלוחת הסקרים כלולה במערכת'],
              variants: [{
                when: 'surveysOff',
                summary: 'שלוחת הסקרים לא כלולה במערכת שלכם',
                details: ['שלוחת הסקרים לא כלולה במערכת שלכם, ולכן ההקשה משמיעה הודעה שהסקרים לא זמינים.'],
              }],
              web: { route: '/settings/page1', label: 'הגדרות כלליות' },
              keywords: ['סקרים', 'יצירת סקר', 'הרשאת סקרים', 'מי יוצר סקרים'],
            },
          ],
        },
        {
          id: 'main-7-9', key: '9', title: 'הודעות ממתינות לאישור', summary: 'הודעות של משתמשים לפני פרסום', role: 'managers',
          playback: 'full',
          details: [`הודעה שלא אושרה נמחקת אחרי ${RETENTION.approvalDays} ימים.`],
          keywords: ['אישור', 'ממתין', 'לאשר', 'בקרה'],
          children: [
            {
              id: 'main-7-9-8', key: '8', title: 'אפשרויות להודעה ממתינה', summary: 'בזמן השמעת הודעה, מקש 8', role: 'managers',
              optionGroups: [
                {
                  title: 'בתפריט האפשרויות',
                  items: [
                    { key: '1', text: 'פרטי השולח' },
                    { key: '2', text: 'מחיקה' },
                    { key: '3', text: 'תשובה אישית לשולח' },
                    { key: '4', text: 'אישור ופרסום' },
                    { key: '*', text: 'חזרה להשמעה' },
                  ],
                },
                { title: 'באישור ופרסום', items: [{ key: '1', text: 'פרסום בלי צינתוק' }, { key: '2', text: 'פרסום עם צינתוק' }] },
              ],
              keywords: ['לאשר', 'פרסום', 'דחייה'],
            },
          ],
        },
      ],
    },

    // ------------------------------------------------------------------ 8
    {
      id: 'main-8',
      key: '8',
      title: 'חדר ועידה',
      summary: 'שיחה קבוצתית חיה בין חברי המערכת',
      role: 'all',
      details: [
        'לכל מערכת חדר ועידה אחד.',
        'אם המנהל קבע שרק מנהלים פותחים את החדר, משתמשים ממתינים עם מוזיקה עד שמנהל נכנס. בזמן ההמתנה, 8 מחזיר לתפריט.',
        'בוועידה רגילה, כשהמנהל האחרון יוצא, החדר נסגר.',
        'בכניסה הראשונה מושמע הסבר על המקשים שבתוך החדר.',
        'בעל המערכת יכול להגדיר את החדר כשידור חי, בדף ההגדרות באתר: הראשון שנכנס מדבר, וכל השאר מאזינים מושתקים, בלי אפשרות לבטל את ההשתקה. אם רק מנהלים פותחים את החדר, מדבר המנהל הראשון שנכנס.',
        'מאזין בשידור חי: 4 ו-6 לשינוי עוצמת השמע, 0 לשמיעת מספר המשתתפים, ו-8 ליציאה.',
        'כשהדובר יוצא, השידור מסתיים לכולם, וכל המאזינים שומעים שהשידור הסתיים.',
      ],
      requires: ['חדר הוועידה פעיל במערכת'],
      keywords: ['ועידה', 'שיחה קבוצתית', 'לדבר', 'שיעור', 'חי', 'שידור', 'שידור חי', 'מושתק', 'השתקה', 'מאזין'],
      variants: [{
        when: 'telebip',
        title: 'הודעה למנהלים',
        summary: 'הקלטה שרק המנהלים שומעים',
        details: [
          'במערכת שלכם מקש 8 מעביר ישירות להשארת הודעה למנהלים, כמו 5 ואז 5.',
          'המנהלים שומעים את ההודעה בתפריט הניהול, בשלוחה 7-7.',
        ],
        optionGroups: [
          {
            title: 'אחרי ההקלטה',
            items: [
              { key: '0', text: 'שליחה עם צינתוק למנהלים' },
              { key: '4', text: 'שליחה בלי צינתוק' },
              { key: '1', text: 'השמעה לפני השליחה' },
              { key: '2', text: 'הקלטה מחדש' },
              { key: '3', text: 'מחיקה' },
            ],
          },
        ],
        requires: [],
        keywords: ['הודעה למנהל', 'פנייה למנהל', 'הקלטה למנהלים', 'מנהלים'],
      }],
    },

    // ------------------------------------------------------------------ 9
    {
      id: 'main-9',
      key: '9',
      title: 'פתיחת מערכת חדשה',
      summary: 'הקמת מערכת משלכם בטלפון',
      role: 'all',
      details: [
        'המערכת מלווה אתכם בהקמה: בחירת שפה, סיסמת ניהול, הרשאות הקלטה והקלטת פתיח. בסיום מוקצה למערכת מספר טלפון.',
        'אפשר לפתוח מערכת אחת ביממה מכל מספר.',
      ],
      keywords: ['מערכת חדשה', 'הקמה', 'פתיחה', 'לפתוח'],
    },

    // ------------------------------------------------------------------ 10
    {
      id: 'main-10',
      key: '10',
      title: 'מועדפים',
      summary: 'ההודעות ששמרתם',
      role: 'all',
      details: ['מוסיפים הודעה למועדפים בזמן ההשמעה: 8 ואז 7 בשלוחת ההודעות, או 8 ואז 5 בהודעות אישיות.'],
      keywords: ['מועדפים', 'שמורות', 'שמרתי'],
      children: [
        { id: 'main-10-1', key: '1', title: 'הסבר על המועדפים', summary: 'איך מוסיפים ומסירים', role: 'all', keywords: ['הסבר'] },
        {
          id: 'main-10-2', key: '2', title: 'השמעת המועדפים', summary: 'סולמית בזמן ההשמעה מסירה הודעה', role: 'all',
          playback: 'favourites',
          optionGroups: [{ title: 'בהסרה', items: [{ key: '1', text: 'אישור ההסרה' }, { key: '2', text: 'ביטול' }] }],
          keywords: ['השמעה', 'הסרה ממועדפים'],
        },
      ],
    },

    // ------------------------------------------------------------------ 11
    {
      id: 'main-11',
      key: '11',
      title: 'סקרים',
      summary: 'הצבעה בסקרים, תוצאות, ויצירת סקרים',
      role: 'all',
      details: [
        'בכניסה מושמע מספר הסקרים הפתוחים, וכמה מהם חדשים וממתינים להצבעה שלכם.',
      ],
      requires: ['שלוחת הסקרים כלולה במערכת'],
      variants: [{
        when: 'surveysOff',
        summary: 'לא כלולה במערכת שלכם',
        details: ['שלוחת הסקרים לא כלולה במערכת שלכם. הקשה על 11 משמיעה הודעה שהסקרים לא זמינים.'],
        children: [],
      }],
      keywords: ['סקר', 'סקרים', 'הצבעה', 'משאל', 'תוצאות', 'שלוחה 11'],
      children: [
        {
          id: 'main-11-1', key: '1', title: 'סקרים פתוחים', summary: 'הצבעה, שינוי הצבעה ותוצאות ביניים', role: 'all',
          details: [
            'על כל סקר מושמע מספר הסקר, והאם כבר הצבעתם בו.',
            'בהצבעה מושמעת שאלת הסקר. מקישים את מקש התשובה, ושומעים איזה מקש בחרתם ושההצבעה נקלטה.',
            'אם הסקר מאפשר יותר מהצבעה אחת, אפשר להמשיך ולהצביע למקשים נוספים, עד המספר שנקבע.',
            'סקר שמועד הסיום שלו עבר כבר לא מקבל הצבעות.',
          ],
          optionGroups: [
            { title: 'ברשימת הסקרים', items: [{ key: '1', text: 'כניסה לסקר' }, { key: '2', text: 'שמיעת שאלת הסקר' }, { key: '6', text: 'הסקר הבא' }, { key: '4', text: 'הסקר הקודם' }, { key: '*', text: 'יציאה' }] },
            { title: 'בתוך סקר', items: [{ key: '1', text: 'הצבעה' }, { key: '2', text: 'שינוי הצבעה (אם הסקר מאפשר)' }, { key: '3', text: 'תוצאות ביניים (אם הסקר מאפשר)' }, { key: '4', text: 'פרטי הסקר' }, { key: '*', text: 'חזרה לרשימת הסקרים' }] },
          ],
          keywords: ['להצביע', 'הצבעה', 'שינוי הצבעה', 'תוצאות ביניים', 'סקר פתוח'],
        },
        {
          id: 'main-11-2', key: '2', title: 'היסטוריית סקרים סגורים', summary: 'תוצאות של סקרים שהסתיימו', role: 'all',
          details: ['על כל סקר מושמע מספר הסקר. בסקר סגור אפשר תמיד לשמוע את התוצאות.'],
          optionGroups: [{ title: 'בכל סקר', items: [{ key: '1', text: 'שמיעת התוצאות' }, { key: '2', text: 'שמיעת שאלת הסקר' }, { key: '3', text: 'פרטי הסקר' }, { key: '6', text: 'הסקר הבא' }, { key: '4', text: 'הסקר הקודם' }, { key: '*', text: 'חזרה לתפריט הסקרים' }] }],
          keywords: ['סקרים סגורים', 'היסטוריית סקרים', 'תוצאות סופיות', 'סקר שהסתיים'],
        },
        {
          id: 'main-11-9', key: '9', title: 'ניהול שלוחת הסקרים', summary: 'יצירת סקר חדש, והסקרים שלי', role: 'all',
          details: ['מי שאין לו הרשאה ומקיש 9 שומע שאין לו הרשאה, וחוזר לתפריט הסקרים.'],
          requires: ['מנהלים ובעלים תמיד. שאר המאזינים רק אם בעל המערכת אפשר לכולם ליצור סקרים'],
          keywords: ['ניהול סקרים'],
          children: [
            {
              id: 'main-11-9-1', key: '1', title: 'יצירת סקר חדש', summary: 'הקלטת שאלה, מקשי תשובה והגדרות', role: 'all',
              details: [
                'מקליטים את שאלת הסקר ומסיימים בסולמית. אחר כך אפשר לאשר, לשמוע את ההקלטה, להקליט מחדש או למחוק.',
                'מקישים את מקשי התשובה, עם כוכבית בין מקש למקש (לפחות 2 מקשים), ומסיימים בסולמית.',
                'בוחרים כמה פעמים כל משתתף יכול להצביע, האם לשמור את מספר הטלפון של המצביע או להצביע אנונימית, האם לשמוע תוצאות ביניים בזמן אמת או רק אחרי שהסקר נסגר, והאם אפשר לשנות הצבעה.',
                'משך הסקר: מספר שעות, מספר ימים, או ללא הגבלת זמן עם סגירה ידנית.',
                'הפעלה: מיד או במועד מאוחר יותר, עם צינתוק לחברים או בלעדיו. בסיום מושמע מספר הסקר.',
              ],
              keywords: ['סקר חדש', 'יצירת סקר', 'הקלטת שאלה', 'הצבעה אנונימית', 'צינתוק על סקר'],
            },
            {
              id: 'main-11-9-2', key: '2', title: 'הסקרים שלי', summary: 'עריכה, שכפול, תוצאות ומחיקה', role: 'all',
              details: [
                'משתמש רגיל רואה כאן את הסקרים שהוא יצר. מנהלים ובעלים רואים את כל הסקרים של המערכת.',
                'מחיקת סקר אפשרית למי שיצר אותו, ולמנהלים ולבעלים גם אם לא הם יצרו אותו. לפני המחיקה מתבקש אישור.',
              ],
              optionGroups: [
                { title: 'בכל סקר', items: [{ key: '1', text: 'עריכת פרטי הסקר' }, { key: '2', text: 'שכפול הסקר' }, { key: '3', text: 'שמיעת התוצאות' }, { key: '4', text: 'מחיקת הסקר' }, { key: '*', text: 'חזרה' }] },
                { title: 'עריכה', items: [{ key: '1', text: 'סגירה או פתיחה מחדש' }, { key: '2', text: 'מספר הצבעות למשתתף' }, { key: '3', text: 'תוצאות ביניים בזמן שהסקר פעיל' }, { key: '4', text: 'אפשרות לשנות הצבעה' }, { key: '5', text: 'משך הסקר' }, { key: '*', text: 'חזרה' }] },
              ],
              notices: ['מחיקה מוחקת את הסקר ואת כל ההצבעות שלו לצמיתות.'],
              keywords: ['הסקרים שלי', 'עריכת סקר', 'שכפול סקר', 'מחיקת סקר', 'סגירת סקר', 'פתיחה מחדש'],
            },
          ],
        },
      ],
    },

    // ------------------------------------------------------------------ 32
    {
      id: 'main-32',
      key: '32',
      title: 'הפנייה פנימית',
      summary: 'מעבר למערכת אחרת בתוך מערכות קליקול',
      role: 'all',
      details: [
        'הפנייה פנימית של השיחה למערכת קליקול אחרת, בלי לנתק ולחייג מחדש.',
        'השלוחה פעילה רק במערכות שביקשו אותה והוגדרה להן במיוחד. בשאר המערכות ההקשה מחזירה לתפריט הראשי.',
      ],
      keywords: ['הפנייה', 'הפניה', 'העברה פנימית', 'קליקול', 'מערכת אחרת', 'העברה', 'יעד'],
    },
  ],
};

// ----------------------------------------------------------------------------
// ממשק הניהול
// roles: מי רואה את העמוד בממשק (לפי role של החשבון: user / agent / admin)
// ----------------------------------------------------------------------------
export const webPages = [
  {
    id: 'web-home',
    title: 'דף הבית',
    route: '/home',
    icon: 'mdi-view-dashboard-outline',
    roles: ['user', 'agent', 'admin'],
    summary: 'כרטיסי ניווט לכל אזורי הממשק.',
    items: [],
  },
  {
    id: 'web-account',
    title: 'אזור אישי',
    route: '/account',
    icon: 'mdi-account-circle-outline',
    roles: ['user', 'agent', 'admin'],
    summary: 'פרטי החשבון, סיסמאות והגדרות אבטחה.',
    items: [
      { id: 'web-account-details', title: 'פרטי המשתמש', text: 'שם, שם העסק, ת״ז או ח״פ, טלפון, אימייל וכתובת.', keywords: ['פרטים', 'אימייל', 'כתובת', 'עסק'] },
      { id: 'web-account-phonepass', title: 'סיסמה טלפונית', text: 'סיסמת הניהול שמקישים בשלוחה 7.', keywords: ['סיסמת ניהול', 'סיסמה טלפונית'], phoneNode: 'main-7' },
      { id: 'web-account-webpass', title: 'סיסמת אתר הניהול', text: 'החלפת הסיסמה לכניסה לממשק. לפחות 9 תווים.', keywords: ['סיסמה', 'החלפת סיסמה', 'כניסה לאתר'] },
      { id: 'web-account-token', title: 'טוקן API', text: 'המפתח לחיבור מערכות חיצוניות לממשק ה-API.', keywords: ['api', 'טוקן', 'מפתח'] },
      { id: 'web-account-ip', title: 'כתובת IP קבועה', text: 'כתובת שתיכנס לממשק בלי אימות דו-שלבי.', keywords: ['ip', 'אימות דו שלבי', 'קוד אימות'] },
      { id: 'web-account-listen', title: 'שמירת מיקום אחרון', text: 'מתי המערכת שומרת היכן הפסקתם להאזין: רק בניתוק שיחה, או תמיד.', keywords: ['מיקום אחרון', 'המשך'], phoneNode: 'main-hash' },
    ],
  },
  {
    id: 'web-online',
    title: 'שיחות און ליין',
    route: '/on-line-users',
    icon: 'mdi-phone-in-talk-outline',
    roles: ['user', 'agent', 'admin'],
    summary: 'מי מחובר עכשיו, ומה היה העומס.',
    items: [
      { id: 'web-online-live', title: 'שיחות פעילות', text: 'רשימת השיחות הפעילות עכשיו: מי מתקשר ובאיזו שלוחה הוא נמצא. אפשר לסנן לנכנסות או ליוצאות ולחפש.', keywords: ['פעילות', 'עכשיו', 'מחוברים', 'חי'] },
      { id: 'web-online-hangup', title: 'ניתוק שיחה', text: 'ניתוק שיחה פעילה מתוך הרשימה.', keywords: ['ניתוק', 'לנתק'] },
      { id: 'web-online-daily', title: 'סטטיסטיקות יומיות', text: 'שיא השיחות, ממוצע שעתי ושעת השיא לתאריך שבוחרים.', keywords: ['שיא', 'עומס', 'סטטיסטיקה', 'יומי'] },
      { id: 'web-online-monthly', title: 'סטטיסטיקות חודשיות', text: 'שיא חודשי, ממוצע יומי ויום השיא.', keywords: ['חודשי', 'סטטיסטיקה'] },
    ],
  },
  {
    id: 'web-reports',
    title: 'דוח שיחות',
    route: '/reports',
    icon: 'mdi-file-chart-outline',
    roles: ['user', 'agent', 'admin'],
    summary: 'דוחות חודשיים של דקות וכניסות.',
    items: [
      { id: 'web-reports-monthly', title: 'דוחות חודשיים', text: 'צפייה בדקות ובמספר הכניסות לכל חודש, והורדת הדוח.', keywords: ['דוח', 'דקות', 'כניסות', 'הורדה', 'חודשי'] },
    ],
  },
  {
    id: 'web-history',
    title: 'היסטוריית מידע ושימושים',
    route: '/call-history',
    icon: 'mdi-history',
    roles: ['user', 'agent', 'admin'],
    summary: 'כל השיחות שהיו, ומה עשה כל מתקשר.',
    items: [
      { id: 'web-history-list', title: 'היסטוריית שיחות', text: 'שיחות לפי טווח תאריכים, עם חיפוש לפי שם או מספר.', keywords: ['היסטוריה', 'שיחות קודמות', 'מי התקשר'] },
      { id: 'web-history-actions', title: 'פעולות בשיחה', text: '"הצג פעולות" מציג את מסלול השלוחות של המתקשר ואת הזמן בכל אחת.', keywords: ['מסלול', 'שלוחות', 'פעולות', 'מה עשה'] },
    ],
  },
  {
    id: 'web-upload',
    title: 'העלאת קבצים',
    route: '/upload/page1',
    icon: 'mdi-cloud-upload-outline',
    roles: ['user', 'agent', 'admin'],
    summary: 'הקלטות התפריט וכל ההודעות במערכת.',
    items: [
      {
        id: 'web-upload-menu', title: 'ניהול הקלטת תפריט', route: '/upload/page1',
        text: 'העלאת הודעת פתיחה והודעה זמנית, השמעה והורדה. לכל אחת קובעים אם היא עקיפה (אפשר להקיש תוך כדי) או בלתי עקיפה, ולהודעה הזמנית מועד סיום.',
        keywords: ['פתיח', 'הודעה זמנית', 'עקיף', 'בלתי עקיף', 'תפריט', 'דילוג'], phoneNode: 'main-7-5',
      },
      {
        id: 'web-upload-messages', title: 'ניהול שלוחת הודעות', route: '/upload/page2',
        text: 'כל ההודעות במערכת: שלוחת הודעות, אישיות, למנהלים, מתוזמנות, משותפות וממתינות לאישור. השמעה במהירויות שונות, הורדה, מחיקה בודדת או מרובה, והעברת הודעה ממתינה לשלוחה.',
        keywords: ['הודעות', 'הקלטות', 'רשימה', 'מחיקה', 'הורדה', 'ממתין לאישור'], phoneNode: 'main-1',
      },
      {
        id: 'web-upload-add', title: 'הוספת הקלטה', route: '/upload/page2',
        text: 'העלאת קבצי שמע או וידאו עד 100MB, גם כמה בבת אחת. אפשר לשלוח צינתוק, לתזמן פרסום ולהגביל את זמן ההשמעה.',
        keywords: ['העלאה', 'הוספה', 'קובץ', 'וידאו', 'mp3', 'תזמון'], phoneNode: 'main-2',
      },
      {
        id: 'web-upload-stats', title: 'סטטיסטיקת שמיעה', route: '/upload/page2',
        text: `מי האזין להודעה, מתי, וכמה אחוזים ממנה. הנתונים נשמרים ${RETENTION.listenStatsMonths} חודשים.`,
        keywords: ['סטטיסטיקה', 'מי שמע', 'האזנות', 'אחוז'], phoneNode: 'main-1-8-8',
      },
      {
        id: 'web-upload-deleted', title: 'קבצים שנמחקו', route: '/upload/page3',
        text: `הודעות שנמחקו, עם אפשרות השמעה, הורדה ומחיקה לצמיתות. אפשר לסמן כמה הודעות, או את כולן, ולמחוק אותן לצמיתות בבת אחת. נמחקות אוטומטית אחרי ${RETENTION.deletedDays} יום. הודעות שנמחקו כבר לא נספרות באחסון של החבילה.`,
        keywords: ['נמחקו', 'שחזור', 'סל מחזור', 'מחיקה לצמיתות', 'מחיקה מרובה', 'סימון', 'פינוי מקום', 'אחסון מלא'],
      },
      {
        id: 'web-upload-temp', title: 'קבצים זמניים', route: '/upload/page4',
        text: `הקלטות שנשמרו כשהשיחה התנתקה לפני הפרסום. אפשר להעביר אותן לשלוחת ההודעות. נמחקות אחרי ${RETENTION.tempDays} יום.`,
        keywords: ['זמני', 'ניתוק', 'העברה'], phoneNode: 'main-0-5',
      },
    ],
  },
  {
    id: 'web-subscribers',
    title: 'רשימת תפוצה',
    route: '/subscribes-list',
    icon: 'mdi-account-group-outline',
    roles: ['user', 'agent', 'admin'],
    summary: 'אנשי הקשר שיכולים להתקשר למערכת.',
    items: [
      {
        id: 'web-subs-add', title: 'הוספה ועריכה של איש קשר',
        text: 'שם, טלפון, מדינה לקבלת צינתוק, הרשאה (משתמש או מנהל) ומתי לשמור מיקום אחרון.',
        keywords: ['הוספה', 'עריכה', 'איש קשר', 'מנהל', 'הרשאה'], phoneNode: 'main-7-1',
      },
      {
        id: 'web-subs-import', title: 'ייבוא מאקסל',
        text: 'העלאת קובץ xlsx לפי התבנית להורדה: שם, טלפון, קוד מדינה, הרשאה ואישור צינתוק. אפשר לשלוח צינתוק לאנשי הקשר החדשים.',
        keywords: ['אקסל', 'ייבוא', 'excel', 'קובץ', 'תבנית', 'הרבה מספרים'],
      },
      { id: 'web-subs-export', title: 'ייצוא לאקסל', text: 'הורדת כל אנשי הקשר לקובץ.', keywords: ['ייצוא', 'אקסל', 'הורדה'] },
      {
        id: 'web-subs-tzintuk', title: 'הגדרות צינתוק לאיש קשר',
        text: 'אישור קבלת צינתוקים והשתקה עד תאריך, לכל איש קשר בנפרד.',
        keywords: ['צינתוק', 'השתקה', 'אישור'], phoneNode: 'main-4',
      },
      {
        id: 'web-subs-callall', title: 'צינתוק לכולם',
        text: 'שליחת צינתוק לכל מי שאישר קבלת צינתוקים, גם בלי הודעה חדשה - למשל כדי להזכיר לכולם להתקשר.',
        keywords: ['צינתוק לכולם', 'התראה', 'לכולם', 'צינתוק בלי הודעה', 'צלצול'],
      },
    ],
  },
  {
    id: 'web-settings',
    title: 'הגדרות',
    route: '/settings/page1',
    icon: 'mdi-cog-outline',
    roles: ['user', 'agent', 'admin'],
    summary: 'כל ההגדרות של המערכת הטלפונית.',
    items: [
      {
        id: 'web-set-general', title: 'הגדרות כלליות', route: '/settings/page1',
        text: 'הרשאות הקלטה ציבורית ופרטית, שפה, הוספת חברים ע״י כל משתמש, קו פתוח לכולם, שיתוף הודעות, וחדר הוועידה - כולל ועידה רגילה או שידור חי.',
        keywords: ['הרשאות', 'שפה', 'שיתוף', 'ועידה', 'קו פתוח', 'שידור חי'], phoneNode: 'main-7-8',
      },
      {
        id: 'web-set-security', title: 'אבטחה ופרטיות', route: '/settings/page1',
        text: 'סיסמת כניסה למערכת, ומי מדלג עליה: אף אחד, מנהלים ובעלים, בעלים בלבד, או משתמשים שנוספו ידנית. וגם מחיקה אוטומטית של הקלטות אחרי 30 יום (גם בטלפון: שלוחה 7, ואז 8, ואז 8).',
        keywords: ['סיסמת כניסה', 'דילוג על סיסמה', 'מחיקה אוטומטית', 'פרטיות'], phoneNode: 'main-7-6',
      },
      {
        id: 'web-set-timezone', title: 'אזור זמן', route: '/settings/page1',
        text: 'אזור הזמן שלפיו מוצגים התאריכים ומתוזמנות ההודעות.',
        keywords: ['אזור זמן', 'שעון', 'utc', 'חו"ל'],
      },
      {
        id: 'web-set-ai', title: 'העוזר הקולי (שלוחה 3)', route: '/settings/page1',
        text: 'האם המאזינים יכולים לשוחח עם העוזר הקולי בשלוחה 3. העוזר נכלל רק בחלק מהמערכות. למי הוא פתוח תלוי בחבילה: בחלק מהחבילות לכל המאזינים, ובחלק רק למאזינים עם מנוי רב קול. כשהעוזר כבוי, שלוחה 3 משמיעה את הוראות השימוש. אם האפשרות לא מופיעה בעמוד, העוזר לא כלול במערכת שלכם.',
        keywords: ['עוזר', 'עוזר קולי', 'AI', 'בינה מלאכותית', 'שלוחה 3', 'הפעלה', 'כיבוי', 'חבילה בתשלום', 'שדרוג'], phoneNode: 'main-3',
      },
      {
        id: 'web-set-surveys', title: 'יצירת סקרים (שלוחה 11)', route: '/settings/page1',
        text: 'מי יכול ליצור סקרים בשלוחת הסקרים: כל המאזינים, או מנהלים בלבד. מנהלים ובעלים יכולים תמיד. מי שיוצר סקר יכול גם לשלוח עליו צינתוק לכל החברים. האפשרות מופיעה בעמוד רק כששלוחת הסקרים כלולה במערכת. אפשר לשנות אותה גם בטלפון: שלוחה 7, ואז 8, ואז 9.',
        keywords: ['סקרים', 'יצירת סקר', 'הרשאת סקרים', 'מי יוצר סקרים', 'שלוחה 11'], phoneNode: 'main-7-8-9',
      },
      {
        id: 'web-set-tzintuk', title: 'צינתוק', route: '/settings/page2',
        text: 'זמן הצלצול, המספר המזהה בשיחה יוצאת, והשתקה: עד תאריך, כולל או בלי הודעות אישיות, ולוח זמנים קבוע לכל יום בשבוע.',
        keywords: ['צינתוק', 'זמן צלצול', 'מספר מזהה', 'השתקה', 'שבת', 'לילה', 'לוח זמנים'], phoneNode: 'main-7-8-4',
      },
      {
        id: 'web-set-records', title: 'הקלטות מערכת', route: '/settings/page3',
        text: 'כל ההקלטות שהמערכת משמיעה, לפי קוד ותוכן. אפשר להחליף כל הקלטה בהקלטה משלכם, ולמחוק אותה כדי לחזור לברירת המחדל.',
        keywords: ['הקלטות מערכת', 'החלפה', 'מותאם אישית', 'קוד הודעה', 'קול'],
      },
      {
        id: 'web-set-plan', title: 'פרטי חשבון וחיוב', route: '/settings/page4',
        text: 'החבילה הנוכחית, מועד החיוב הבא, ערוצים, אנשי קשר ואחסון, ושדרוג חבילה.',
        keywords: ['חבילה', 'תשלום', 'חיוב', 'שדרוג', 'אחסון', 'ערוצים'],
      },
      {
        id: 'web-set-api', title: 'API ו-Webhooks', route: '/settings/page5',
        text: 'שליחת נתונים לכתובת חיצונית כשקורה אירוע, למשל בסיום הקלטה בשלוחה 2 או בהקשה על 1-8-9. לכל פעולה: מצב, כתובת וכותרות.',
        keywords: ['api', 'webhook', 'אינטגרציה', 'חיבור'], phoneNode: 'main-1-8-9',
      },
    ],
  },
  {
    id: 'web-plans',
    title: 'חבילות, פרסומות ומנוי רב קול',
    route: '/settings/page4',
    icon: 'mdi-package-variant-closed',
    roles: ['user', 'agent', 'admin'],
    summary: 'מה כוללת כל חבילה, מתי שומעים פרסומות, ומה זה מנוי רב קול.',
    items: [
      {
        id: 'web-plans-ravkol', title: 'החבילה החינמית', route: '/settings/page4',
        text: 'מערכת חדשה נפתחת בחבילה החינמית: מתקשר בלי מנוי רב קול שומע פרסומת קצרה, לכל היותר פעם ב-20 דקות, בכניסה למערכת או בזמן האזנה להודעות. העוזר הקולי בחבילה הזו פתוח רק למתקשרים עם מנוי רב קול.',
        keywords: ['החבילה החינמית', 'חבילת רב קול', 'חינמית', 'חינם', 'פרסומת', 'פרסומות'],
      },
      {
        id: 'web-plans-member', title: 'מנוי רב קול (של המתקשר)',
        text: 'מנוי אישי שהמתקשר עצמו משלם עליו, כדי לא לשמוע פרסומות. המנוי שייך למתקשר ולא למערכת, והוא חל בכל מערכת שהחבילה שלה כוללת פרסומות. בנוסף, מתקשר עם מנוי רב קול יכול להשתמש בעוזר הקולי במערכות שבהן העוזר זמין ומופעל.',
        keywords: ['מנוי רב קול', 'מנוי', 'בלי פרסומות', 'פרסומות'], phoneNode: 'main-3',
      },
      {
        id: 'web-plans-paid', title: 'חבילה בתשלום', route: '/settings/page4',
        text: 'חבילה בתשלום מאפשרת יותר אנשי קשר, יותר אחסון לשמירת הודעות, ועוד. אפשר לשדרג בעמוד פרטי החשבון, או בטלפון: שלוחה 7, ואז 8, ואז 3. כל חבילה קובעת גם אם יש בה פרסומות למתקשרים בלי מנוי רב קול, ולמי פתוח העוזר הקולי: לכל המתקשרים, או רק למנויי רב קול. יש חבילות בתשלום בלי פרסומות, ויש חבילות בתשלום שכוללות פרסומות (למשל חבילה שמוסיפה בעיקר אחסון).',
        keywords: ['שדרוג', 'חבילה בתשלום', 'בלי פרסומות', 'אנשי קשר', 'אחסון', 'תשלום'],
      },
      {
        id: 'web-plans-storage', title: 'אחסון, והתראה לפני שהוא מתמלא', route: '/settings/page4',
        text: 'לכל חבילה יש מגבלת אחסון להודעות, ואת המצב הנוכחי רואים בעמוד פרטי החשבון. כשנשארים פחות מ-300MB פנויים, בעל המערכת מקבל שיחה קולית לטלפון שלו, עם הודעה שהאחסון עומד להיגמר. השיחה נשלחת פעם אחת בכל חבילה: אחרי מעבר לחבילה אחרת, תגיע שיחה נוספת אם המקום שוב יתקרב לסוף. כשהאחסון מלא, אי אפשר להקליט או להעלות הודעות חדשות. כדי לפנות מקום מוחקים הודעות - הודעות שנמחקו כבר לא נספרות באחסון, כך שהמקום מתפנה מיד - או עוברים לחבילה עם יותר אחסון.',
        keywords: ['אחסון', 'אחסון מלא', 'נגמר המקום', 'התראת אחסון', 'שיחה על אחסון', 'פינוי מקום', 'מגבלת אחסון', 'אי אפשר להקליט'],
      },
      {
        id: 'web-plans-ai', title: 'מי יכול להשתמש בעוזר הקולי',
        text: 'תלוי בחבילה של המערכת: בחלק מהחבילות כל מתקשר, ובחלק רק מתקשר עם מנוי רב קול אישי. זה בתנאי שהעוזר זמין במערכת - הוא לא כלול בכל המערכות - ושבעל המערכת הפעיל אותו בהגדרות. חבילה בתשלום או מנוי רב קול לבדם לא מבטיחים שהעוזר יהיה זמין, ולכן כדאי לבדוק מול שירות הלקוחות לפני שמשדרגים בשביל העוזר.',
        keywords: ['עוזר קולי', 'AI', 'זכאות'], phoneNode: 'main-3',
      },
      {
        id: 'web-plans-support', title: 'שירות הלקוחות של קליקול',
        text: 'לשאלות על חבילות, תשלום, שדרוג, או כל תקלה: 03-3728728.',
        keywords: ['שירות לקוחות', 'תמיכה', 'טלפון', 'קליקול', 'תקלה', 'בעיה', 'לדבר עם נציג'],
      },
    ],
  },
  {
    id: 'web-surveys',
    title: 'סקרים',
    route: '/surveys',
    icon: 'mdi-poll',
    roles: ['user', 'agent', 'admin'],
    summary: 'יצירת סקרים, עריכה, השמעת שאלת הסקר ותוצאות.',
    items: [
      { id: 'web-surveys-audio', title: 'השמעת שאלת הסקר', route: '/surveys', text: 'ליד כל סקר יש כפתור השמעה של שאלת הסקר, גם כשהיא הוקלטה בטלפון. כפתור מושבת אומר שלסקר אין הקלטת שאלה.', keywords: ['השמעת סקר', 'שאלת הסקר', 'לשמוע את הסקר', 'הקלטת הסקר'] },
    ],
  },
  {
    id: 'web-agent',
    title: 'פאנל ניהול סוכן',
    route: '/agent/page1',
    icon: 'mdi-account-tie-outline',
    roles: ['agent'],
    summary: 'ניהול המערכות של הלקוחות שלכם.',
    items: [
      { id: 'web-agent-users', title: 'ניהול משתמשים', route: '/agent/page1', text: 'יצירה ועריכה של מערכות, חיבור קווים, הרשאות (כולל העוזר הקולי), ומעבר לחשבון של לקוח.', keywords: ['לקוחות', 'מערכות', 'מעבר למשתמש'] },
      { id: 'web-agent-numbers', title: 'מאגר מספרים', route: '/agent/page2', text: 'המספרים שמוקצים לכם.', keywords: ['מספרים', 'קווים'] },
      { id: 'web-agent-search', title: 'חיפוש מספר', route: '/agent/page3', text: 'איתור מערכת לפי מספר.', keywords: ['חיפוש', 'מספר'] },
    ],
  },
  {
    id: 'web-admin',
    title: 'פאנל ניהול כללי',
    route: '/admin/page1',
    icon: 'mdi-shield-crown-outline',
    roles: ['admin'],
    summary: 'ניהול כל המערכות בפלטפורמה.',
    items: [
      { id: 'web-admin-users', title: 'ניהול משתמשים', route: '/admin/page1', text: 'כל המערכות: יצירה, עריכה, קווים, הרשאות (כולל העוזר הקולי) ומעבר לחשבון.', keywords: ['מערכות', 'משתמשים'] },
      { id: 'web-admin-numbers', title: 'מאגר מספרים', route: '/admin/page2', text: 'הוספה, שיוך לסוכנים ומחיקה של מספרים, וייצוא.', keywords: ['מספרים', 'שיוך'] },
      { id: 'web-admin-search', title: 'חיפוש מספר', route: '/admin/page3', text: 'איתור מערכת לפי מספר.', keywords: ['חיפוש'] },
      { id: 'web-admin-plans', title: 'חבילות', route: '/admin/page4', text: 'יצירה ועריכה של חבילות: ערוצים, אנשי קשר, אחסון ומחיר.', keywords: ['חבילות', 'מחיר'] },
      { id: 'web-admin-rules', title: 'Rules', route: '/admin/page5', text: 'כללי המרת מספרים בשיחות נכנסות, יוצאות ובמספר המזהה.', keywords: ['rules', 'המרה', 'קידומת'] },
      { id: 'web-admin-logs', title: 'לוגים', route: '/logs', text: 'יומן הפעילות של היום.', keywords: ['לוג', 'יומן', 'תקלה'] },
    ],
  },
];

// ----------------------------------------------------------------------------
// מילים נרדפות לחיפוש. כל קבוצה = מילים שמחפשות זו את זו.
// ----------------------------------------------------------------------------
export const SYNONYMS = [
  ['צינתוק', 'צינתוקים', 'התראה', 'התראות', 'שיחה יוצאת', 'צלצול'],
  ['השתקה', 'השתק', 'להשתיק', 'שקט', 'מושתק'],
  ['הודעה', 'הודעות', 'הקלטה', 'הקלטות', 'קובץ', 'קבצים'],
  ['מחיקה', 'למחוק', 'מחק', 'הסרה', 'להסיר'],
  ['אישי', 'אישית', 'פרטי', 'פרטית'],
  ['מנוי', 'מנויים', 'חבר', 'חברים', 'איש קשר', 'אנשי קשר', 'משתמש', 'משתמשים'],
  ['מנהל', 'מנהלים', 'ניהול', 'אדמין'],
  ['סיסמה', 'סיסמא', 'קוד', 'סיסמאות'],
  ['שיתוף', 'לשתף', 'שתף', 'העברה', 'להעביר'],
  ['פתיח', 'הודעת פתיחה', 'ברוכים הבאים'],
  ['ועידה', 'כנס', 'שיחה קבוצתית'],
  ['לייק', 'אהבתי', 'אהבה'],
  ['האזנה', 'שמיעה', 'להאזין', 'לשמוע', 'השמעה'],
  ['דילוג', 'לדלג', 'עקיף', 'לעקוף'],
  ['מהירות', 'להאיץ', 'מהר', 'להאט', 'לאט'],
];